import { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, useFormikContext } from "formik";
import { Row, Col } from "antd";
import { Form, Select } from "formik-antd";
import {
  ColumnType,
  TextType,
  GroupingType,
  InternalIdType,
  AdvancedType,
  ReportColumnTypes,
  ArrayType,
} from "./mappingTypes";
import { useTranslation } from "react-i18next";
import {
  newMappingSchema,
  sqlColumnMappingSchema,
} from "/app/src/schemas/apps/dataPush/newMappingSchema";
import {
  Integration,
  ReportColumnType,
  Mapping,
  MappingType,
} from "/app/src/models";
import { Error } from "/app/src/types";
import { isSQLType } from "/app/src/helpers";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Component for creating a new mapping. Maps the mapping type to the correct component.
 */
function NewMappingTypes({
  integration,
  isThemed,
  type,
  columnTypes,
  dirty,
  isValid,
}: {
  integration?: Integration;
  isThemed: boolean;
  type: MappingType;
  columnTypes: ReportColumnType[];
  dirty: boolean;
  isValid: boolean;
}) {
  return (
    <>
      {type === "column" && (
        <ColumnType
          isThemed={isThemed}
          columnTypes={columnTypes}
          dirty={dirty}
          isValid={isValid}
        />
      )}
      {type === "advanced" && (
        <AdvancedType
          dirty={dirty}
          isValid={isValid}
          isThemed={isThemed}
          columnTypes={columnTypes}
        />
      )}
      {type === "text" && (
        <TextType integration={integration} dirty={dirty} isValid={isValid} />
      )}
      {type === "boolean" && (
        <TextType dirty={dirty} isValid={isValid} isBoolean />
      )}
      {["grouping", "data", "lines"].includes(type) && (
        <GroupingType dirty={dirty} />
      )}
      {type === "array" && <ArrayType dirty={dirty} />}
      {type === "id" && <InternalIdType dirty={dirty} isValid={isValid} />}
      {type === "reportColumn" && (
        <ReportColumnTypes dirty={dirty} isValid={isValid} />
      )}
    </>
  );
}

/**
 * Component for creating a new mapping. Maps the mapping type to the correct component.
 */
function MappingForm({
  integration,
  columnTypes,
}: {
  integration?: Integration;
  columnTypes: ReportColumnType[];
}) {
  const { dirty, setFieldValue, isValid, values, validateForm } =
    useFormikContext<FormValues>();
  const { t } = useTranslation();
  const [type, setType] = useState<MappingType>(undefined);

  useEffect(() => {
    validateForm();
  }, [validateForm, values.type]);

  const dataPullMaterialOptions = useMemo(() => {
    return [
      { value: "id", label: t("translation:internal_id") },
      { value: "data", label: t("translation:materials") },
      { value: "column", label: t("translation:column") },
      { value: "text", label: t("translation:text") },
      { value: "grouping", label: t("translation:grouping") },
      { value: "array", label: t("translation:array") },
      { value: "advanced", label: t("translation:advanced") },
    ];
  }, [t]);

  const dataPullSQLOrderOptions = useMemo(() => {
    return [
      { value: "id", label: t("translation:internal_id") },
      { value: "column", label: t("translation:column") },
      { value: "text", label: t("translation:text") },
      { value: "advanced", label: t("translation:advanced") },
    ];
  }, [t]);

  const dataPullOrderOptions = useMemo(() => {
    return [
      { value: "id", label: t("translation:internal_id") },
      { value: "data", label: t("translation:orders") },
      { value: "column", label: t("translation:column") },
      { value: "text", label: t("translation:text") },
      { value: "grouping", label: t("translation:grouping") },
      { value: "lines", label: t("translation:order_lines") },
      { value: "array", label: t("translation:array") },
      { value: "advanced", label: t("translation:advanced") },
    ];
  }, [t]);
  const dataPullOrderDeleteOptions = useMemo(() => {
    return [
      { value: "data", label: t("translation:orders") },
      { value: "column", label: t("translation:column") },
      { value: "lines", label: t("translation:order_lines") },
      { value: "grouping", label: t("translation:grouping") },
      { value: "advanced", label: t("translation:advanced") },
    ];
  }, [t]);

  const varianceSQLOptions = useMemo(() => {
    return [
      { value: "column", label: t("translation:column") },
      { value: "grouping", label: t("translation:grouping") },
      { value: "array", label: t("translation:array") },
      { value: "data", label: t("translation:data") },
    ];
  }, [t]);

  const varianceAPIOptions = useMemo(() => {
    return [
      { value: "reportColumn", label: t("translation:report_column") },
      { value: "grouping", label: t("translation:grouping") },
      { value: "array", label: t("translation:array") },
      { value: "data", label: t("translation:data") },
    ];
  }, [t]);

  const dataPushOptions = useMemo(() => {
    return [
      { value: "column", label: t("translation:column") },
      { value: "text", label: t("translation:text") },
      { value: "grouping", label: t("translation:grouping") },
      { value: "boolean", label: t("translation:boolean") },
      { value: "array", label: t("translation:array") },
    ];
  }, [t]);

  const optionsToMap = useMemo(() => {
    if (integration?.appName === "Variance") {
      return isSQLType(integration?.connectionType)
        ? varianceSQLOptions
        : varianceAPIOptions;
    }
    if (integration?.baseTable === "Material") {
      return dataPullMaterialOptions;
    }
    if (integration?.baseTable === "Order") {
      if (integration.type === "delete") {
        return dataPullOrderDeleteOptions;
      }
      if (isSQLType(integration?.connectionType)) {
        return dataPullSQLOrderOptions;
      }
      return dataPullOrderOptions;
    }
    return dataPushOptions;
  }, [
    integration?.appName,
    integration?.baseTable,
    integration.type,
    integration?.connectionType,
    dataPushOptions,
    varianceSQLOptions,
    varianceAPIOptions,
    dataPullMaterialOptions,
    dataPullOrderOptions,
    dataPullOrderDeleteOptions,
    dataPullSQLOrderOptions,
  ]);

  const changeType = useCallback(
    (value: MappingType) => {
      setType(value);
      setFieldValue("value", "");
      setFieldValue("key", "");
    },
    [setFieldValue],
  );

  return (
    <Form>
      <Row justify="start" gutter={16}>
        <Col className="mt-6" span={6}>
          <Form.Item name={"type"}>
            <Select
              name={"type"}
              size="large"
              placeholder={t("translation:select_mapping_type")}
              onChange={changeType}
            >
              {optionsToMap.map((option) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  label={option.label}
                >
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={18}>
          {type && (
            <NewMappingTypes
              integration={integration}
              isThemed={Boolean(integration?.baseTable)}
              type={type}
              columnTypes={columnTypes}
              dirty={dirty}
              isValid={isValid}
            />
          )}
        </Col>
      </Row>
    </Form>
  );
}
/**
 * Format the form value keys to match the API Model. Some complex logic
 * is required to handle the different types of columns and text.
 */
export function formatForm(values, position?: string): Mapping {
  let columnType;
  //columns selected from a report will not be an array. Theme columns
  //will be an array(id stored in the 1 index)
  if (Array.isArray(values.columnTypeId)) {
    columnType = values.columnTypeId[1];
  } else {
    columnType = values?.columnTypeId;
  }
  const ret = {
    ...(values?.parentMappingId && {
      parentMappingId: values?.parentMappingId,
    }),
    key: values?.key,
    value: values?.value,
    type: values?.type,
    isPrivate: values?.isPrivate,
    position: values?.position || position,
  };
  if (["column", "advanced"].includes(values.type)) {
    ret["columnTypeId"] = columnType;
  }
  if (values.type === "text") {
    ret.value = values.value;
    ret.isPrivate = values.isPrivate;
  }
  if (values.type === "advanced") {
    ret.key = values.key.join(",");
  }
  if (values.type === "reportColumn") {
    ret["dataType"] = values["dataType"];
  }
  return ret;
}
interface FormValues {
  key: string | undefined;
  dataType: string | undefined;
  value: MappingType | undefined;
  columnTypeId: number | [string | undefined, number | undefined] | undefined;
  type: MappingType | undefined;
  parentMappingId?: number;
  isPrivate: boolean | undefined;
}

/**
 * Component for creating a new mapping.
 */
export default function NewMapping({
  integration,
  addMapping,
  columnTypes,
  parentId = undefined,
}: {
  integration?: Integration;
  addMapping: (mapping: Mapping) => Promise<Error | { mapping: Mapping }>;
  columnTypes?: ReportColumnType[];
  parentId?: number | undefined;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const initValues: FormValues = {
    key: undefined,
    dataType: undefined,
    value: undefined,
    type: undefined,
    columnTypeId: undefined,
    isPrivate: false,
  };

  /**
   * Handle the form submit. Format the form values and call the addMapping
   * function.
   */
  const handleSubmit = useCallback(
    async (values, actions) => {
      values["parentMappingId"] = parentId;
      const { mappings }: { mappings: Mapping[] } = queryClient.getQueryData([
        "mappings",
        integration.id,
      ]);

      /**
       * Calculates the position of a new mapping by counting existing top-level mappings.
       * @returns {string} The position as a string representing the count of top-level mappings.
       */
      const calculatePosition = (): string => {
        const topLevelCount = mappings.filter(
          (mapping: Mapping) => !mapping.parentMappingId,
        ).length;
        return String(topLevelCount);
      };

      const data = formatForm(values, calculatePosition());
      data["integrationId"] = integration.id;

      if (["id", "data"].includes(data.type)) {
        //check if any mappings already exist with a type of id - only one is allowed
        const mapping = mappings?.find(
          (mapping: Mapping) => mapping.type === data.type,
        );
        if (mapping) {
          actions.setFieldError(
            "type",
            t("translation:mapping_type_already_exists"),
          );
          return;
        }
      }
      await addMapping(data).finally(() => {
        actions.resetForm();
      });
    },
    [addMapping, integration.id, parentId, queryClient, t],
  );
  return (
    <Formik
      initialValues={initValues}
      validationSchema={
        ["IBM2", "MSSQL"].includes(integration?.connectionType)
          ? sqlColumnMappingSchema
          : newMappingSchema
      }
      onSubmit={handleSubmit}
    >
      <MappingForm integration={integration} columnTypes={columnTypes} />
    </Formik>
  );
}
